// THE FLOOR HALF OF MODULE 07 (spec 07 §4.3 floor variant + §4.4), slice 07-A.
//
// Two screens: the queue an inspector works from, and the execution screen
// itself — which is not implemented here at all. `/floor/quality/$id` mounts the
// SAME `InspectionRun` the office mounts (§4.4: "office fallback identical"),
// handed the floor's outbox-backed port. Two implementations of a screen that
// grades measurements would be two graders, and the second one is always the
// one that gets a boundary wrong.
//
// OFFLINE (§9), and what each part of it actually costs:
//   * The QUEUE is a `useCachedRead`: online it is live, offline it renders the
//     last snapshot WITH its age, and on a device that has never loaded it the
//     answer is "connect once", never a blank list that reads as "nothing to
//     inspect". A stale queue is workable; an empty one that is really a cache
//     miss sends an operator away from a drum that needs checking.
//   * The DETAIL is cached per inspection, because §9.8's whole promise —
//     "results sync without photos, completion waits in the outbox" — requires
//     the CHECKLIST to be on the device. The check items travel with the
//     inspection (§5), which is what makes caching one read sufficient.
//   * Every WRITE goes through the one outbox, online or not.
//
// WHAT THIS SCREEN CANNOT DO, said instead of shelled (D-033): it cannot raise
// an inspection against an arbitrary subject. §7 grants floor_worker
// `quality.inspection.create`, but §4.3's floor affordance for it is "scan a
// bobbin QR and open its pending inspection" — a scan resolver that belongs to
// the scan surface — and a free-text control-point-plus-subject form on a
// station tablet is not that. It is named here as the gap it is.

import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import {
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import { useFloorOutbox } from "../hooks/use-floor-outbox";
import { useFloorRunPort } from "../hooks/use-floor-run-port";
import {
  deriveInventoryPermissions,
  useInventoryPermissions,
} from "../../office/inventory/hooks/permissions";
import { deriveQualityPermissions } from "../../office/quality/hooks/permissions";
import { InspectionRun, localeText } from "../../office/quality/run/inspection-run";
import type {
  QualityFailureCause,
  QualityInspection,
  QualityInspectionDetail,
} from "../../office/quality/hooks/quality-queries";
import "./floor-quality.css";

/** The floor's §7 mirror, derived from the SAME role feed every other area
 * reads (`useRoleCodesFeed` through the inventory hook — one fetch per device,
 * one retry policy). Fail-closed while it is in flight; an UNRESOLVED feed and
 * a RESOLVED refusal are different facts, and this returns both. */
export function useFloorQualityGrants(): {
  canRecord: boolean;
  canRead: boolean;
  /** §4.4's sheet (07-B-3): `quality.resolution.rework` — a floor_worker holds
   * none of the three, so on a station tablet the sheet is normally the
   * no-authority sentence; a qc lead signed into the same tablet gets the
   * buttons. */
  canRework: boolean;
  /** `quality.resolution.dispose` — scrap + accept-deviation, qc only. */
  canDispose: boolean;
  /** `quality.hold.place`. */
  canPlaceHold: boolean;
  /** `quality.cut.execute` — §7's "Guided cut" row (07-C). A floor_worker
   * HOLDS this and none of the three above, which is the whole shape of the
   * floor persona: the person at the saw proposes, confirms and executes a cut
   * and takes no disposition at all. */
  canCut: boolean;
  /** `inventory.scan.locate` — MODULE 06's code, read off the same feed, for
   * the §9.6 re-measure the §4.4 sheet offers in place. */
  canRemeasure: boolean;
  isResolved: boolean;
  isFeedError: boolean;
} {
  const feed = useInventoryPermissions();
  const codes = deriveQualityPermissions(feed);
  // Module 06's own codes, derived from the SAME role-code set through module
  // 06's own pure mirror — not read off the hook's `has`, which would make this
  // function depend on a shape rather than on the one fact (the role codes)
  // every other line here derives from.
  const inventoryCodes = deriveInventoryPermissions(feed);
  return {
    canRecord: codes.has("quality.inspection.record"),
    canRead: codes.has("quality.inspection.read"),
    canRework: codes.has("quality.resolution.rework"),
    canDispose: codes.has("quality.resolution.dispose"),
    canPlaceHold: codes.has("quality.hold.place"),
    canCut: codes.has("quality.cut.execute"),
    canRemeasure: inventoryCodes.has("inventory.scan.locate"),
    isResolved: feed.isResolved,
    isFeedError: feed.isFeedError,
  };
}

/** The bounded cause catalog (§5 row 7 — no permission code, unpaginated), held
 * WHOLE on the device: a failing inspection cannot be completed without a
 * cause, so a device that could not list them offline could not finish a check
 * it was standing in front of. */
export function useFloorFailureCauses(): ReturnType<
  typeof useCachedRead<readonly QualityFailureCause[]>
> {
  return useCachedRead<readonly QualityFailureCause[]>({
    queryKey: ["floor", "quality-causes"],
    cacheKey: "quality-causes:all",
    queryFn: async ({ signal }) => {
      const data = unwrap(
        await client.GET("/v1/quality/failure-causes", {
          params: { query: { active: "true" } },
          signal: floorReadSignal(signal),
        }),
        "quality-causes",
      );
      return data.items;
    },
  });
}

// ---------------------------------------------------------------------------
// §4.3 floor — the queue
// ---------------------------------------------------------------------------

export function FloorQualityQueueScreen() {
  const { t, i18n } = useTranslation();
  const grants = useFloorQualityGrants();
  const outbox = useFloorOutbox();

  const queue = useCachedRead<readonly QualityInspection[]>({
    queryKey: ["floor", "quality-queue"],
    cacheKey: "quality-queue:open",
    queryFn: async ({ signal }) => {
      // TWO ASKS, NOT ONE PAGE FILTERED. Open and in-progress are both "mine to
      // finish" and §5's `status` takes ONE value, so the queue asks for each
      // and merges.
      //
      // IT USED TO ASK FOR THE NEWEST 50 OF EVERYTHING AND FILTER HERE, and
      // that is empty-queue-shaped on any tenant that has been running a while:
      // §5 pages newest-first, most inspections end up terminal, and once fifty
      // passed/failed/cancelled rows are newer than the open one the page
      // contains no open work at all — so the screen renders "nothing to
      // inspect" while a drum waits. Caught on deployed staging (M07-S08 at
      // 412 px, 2026-08-17) where the demo tenant had accumulated exactly that;
      // the replica never had the volume to show it. A stale queue is workable,
      // an empty one that is really a paging artefact sends an inspector away.
      const page = async (status: "open" | "in_progress") =>
        unwrap(
          await client.GET("/v1/quality/inspections", {
            params: { query: { status, limit: 50 } },
            signal: floorReadSignal(signal),
          }),
          "quality-queue",
        );
      const [open, inProgress] = await Promise.all([page("open"), page("in_progress")]);
      // Newest first, the order §5 answers in and the order the office queue
      // shows — merging two pages loses it, so it is restored explicitly.
      return [...open.items, ...inProgress.items].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      );
    },
  });

  const rows = queue.data ?? [];

  return (
    <FloorScreen
      wide
      title={t("quality.floor.queueTitle")}
      back={
        <Link to="/floor" className="floor-back" data-testid="quality-floor-back">
          {t("quality.floor.backHome")}
        </Link>
      }
    >
      <SnapshotNote
        source={queue.source}
        cachedAt={queue.cachedAt}
        revalidating={queue.revalidating}
      />

      {/* §4.3's floor offline indicator with a queued count. The shell's own
          sync banner carries the device-wide number; this is the same fact said
          where the work is, which is what an inspector standing at a drum
          reads. */}
      {outbox.pendingCount > 0 ? (
        <p className="floor-quality-queued" role="status" data-testid="quality-floor-queued">
          {t("quality.floor.queuedCount", { count: outbox.pendingCount })}
        </p>
      ) : null}
      {outbox.reviewCount > 0 ? (
        <p className="floor-quality-review" role="alert" data-testid="quality-floor-review">
          {t("quality.floor.needsReview", { count: outbox.reviewCount })}
        </p>
      ) : null}

      {grants.isResolved && !grants.isFeedError && !grants.canRead ? (
        <FloorEmpty title={t("quality.floor.refusedTitle")} body={t("quality.floor.refusedBody")} />
      ) : null}

      {queue.isLoading && rows.length === 0 ? <FloorSkeleton rows={4} /> : null}

      {queue.source === "none" && !queue.online ? (
        <FloorNeedsConnection what={t("quality.floor.queueThing")} />
      ) : null}

      {queue.error !== null && queue.source === "none" && queue.online ? (
        <FloorError
          title={t("quality.floor.queueError")}
          detail={t("quality.floor.queueErrorBody")}
          onRetry={queue.refetch}
        />
      ) : null}

      {!queue.isLoading && queue.source !== "none" && rows.length === 0 ? (
        <FloorEmpty title={t("quality.floor.emptyTitle")} body={t("quality.floor.emptyBody")} />
      ) : null}

      {rows.length > 0 ? (
        <ul className="floor-cards" data-testid="quality-floor-list">
          {rows.map((row) => (
            <li key={row.id}>
              {/* The whole card is the target (D-444 ruling 6), and its three
                  facts sit on three lines: what to check, which inspection and
                  where in the flow, then its state. They used to run together
                  as one underlined string ("Copper receiving — 70 mm²QI-…"). */}
              <Link
                to="/floor/quality/$id"
                params={{ id: row.id }}
                className="floor-card floor-card-link"
                data-testid="quality-floor-row"
              >
                {/* THE LINE DEFECT #128 EXISTS FOR. This is the floor queue on
                    the shop-floor tablet — the point in the product where a
                    Russian inspector reads what they are about to check — and
                    until migration 0042 the server could only send English.
                    `localeText` is the SAME resolver the office run and the
                    cause picker use; the code is the last-resort fallback. */}
                <span className="floor-card-title">
                  {localeText(row.control_point_name_i18n, i18n.language, row.control_point_code)}
                </span>
                <span className="floor-card-meta">
                  {row.inspection_no} · {t(`quality.stage.${row.stage}`)}
                </span>
                <span className="floor-card-chips">
                  <span
                    className={`floor-badge${row.status === "in_progress" ? " floor-badge-info" : ""}`}
                    data-testid="quality-floor-status"
                  >
                    {t(`quality.status.${row.status}`)}
                  </span>
                  {row.is_blocking ? (
                    <span className="floor-quality-flag" data-testid="quality-floor-blocking">
                      {t(`quality.queue.blocks.${row.stage}`)}
                    </span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// §4.4 floor — the execution screen (the SHARED component, floor port)
// ---------------------------------------------------------------------------

export function FloorQualityRunScreen() {
  const { t } = useTranslation();
  const { id } = useParams({ from: "/floor/quality/$id" });
  const grants = useFloorQualityGrants();
  const port = useFloorRunPort(id);
  const causes = useFloorFailureCauses();

  const detail = useCachedRead<QualityInspectionDetail>({
    queryKey: ["floor", "quality-inspection", id],
    cacheKey: `quality-inspection:${id}`,
    queryFn: async ({ signal }) => {
      return unwrap(
        await client.GET("/v1/quality/inspections/{id}", {
          params: { path: { id } },
          signal: floorReadSignal(signal),
        }),
        "quality-inspection",
      );
    },
  });

  return (
    <FloorScreen
      title={t("quality.floor.runTitle")}
      back={
        <Link to="/floor/quality" className="floor-back" data-testid="quality-run-back">
          {t("quality.floor.backQueue")}
        </Link>
      }
    >
      <SnapshotNote
        source={detail.source}
        cachedAt={detail.cachedAt}
        revalidating={detail.revalidating}
      />

      {detail.isLoading && detail.data === undefined ? <FloorSkeleton rows={5} /> : null}

      {detail.source === "none" && !detail.online ? (
        <FloorNeedsConnection what={t("quality.floor.runThing")} />
      ) : null}

      {detail.error !== null && detail.source === "none" && detail.online ? (
        <FloorError
          title={t("quality.floor.runError")}
          detail={t("quality.floor.runErrorBody")}
          onRetry={detail.refetch}
        />
      ) : null}

      {detail.data !== undefined ? (
        <InspectionRun
          detail={detail.data}
          causes={causes.data ?? []}
          // Fail-closed while the feed is in flight; if it FAILED, let the write
          // go and render the server's own refusal (role-codes.ts's rule) rather
          // than turning "we cannot tell" into "you are not permitted" — which
          // on a device that has never resolved the feed would be every cold
          // offline start.
          canRecord={grants.canRecord || grants.isFeedError}
          // Same fail-open-on-feed-ERROR rule as canRecord: the server is the
          // authorisation boundary and renders its own refusal. The sheet's
          // own online gate is separate — a disposition never queues (§4.4).
          failureGrants={{
            canRework: grants.canRework || grants.isFeedError,
            canDispose: grants.canDispose || grants.isFeedError,
            canQuarantine: grants.canPlaceHold || grants.isFeedError,
            canCut: grants.canCut || grants.isFeedError,
            canRemeasure: grants.canRemeasure || grants.isFeedError,
          }}
          port={port}
          shell="floor"
          onApplied={detail.refetch}
        />
      ) : null}
    </FloorScreen>
  );
}
